import React, { useState, useEffect } from 'react';
import { X, Download, Check } from 'lucide-react';
import { Button } from '../ui/button';

const DISMISS_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-installed';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (localStorage.getItem(INSTALLED_KEY) === 'true' || window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
      return;
    }

    // Check dismiss cookie
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDays) return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show popup after 3 seconds
      setTimeout(() => setShowPopup(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setInstalled(true);
      setShowPopup(false);
      localStorage.setItem(INSTALLED_KEY, 'true');
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setInstalled(true);
      localStorage.setItem(INSTALLED_KEY, 'true');
    }
    setDeferredPrompt(null);
    setShowPopup(false);
  };

  const handleDismiss = () => {
    setShowPopup(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />
      <div className="relative bg-white dark:bg-slate-900 dark:border dark:border-white/10 rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 transform animate-bounce-in" data-testid="pwa-install-popup">
        <button onClick={handleDismiss} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10">
          <X className="w-5 h-5 text-slate-400" />
        </button>

        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 overflow-hidden shadow-lg">
            <img src="/icons/icon-192x192.png" alt="A2G Designs" className="w-full h-full object-cover" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Install A2G Designs</h3>
          <p className="text-sm text-slate-500 dark:text-white/50 mb-6">
            Install this app on your device for quick access, offline support, and desktop notifications.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleDismiss}>
              Not Now
            </Button>
            <Button className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0" onClick={handleInstall} data-testid="pwa-install-btn">
              <Download className="w-4 h-4 mr-2" /> Install
            </Button>
          </div>
        </div>

        <style>{`
          @keyframes bounceIn {
            0% { transform: scale(0.5); opacity: 0; }
            60% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
          }
          .animate-bounce-in { animation: bounceIn 0.4s ease-out; }
        `}</style>
      </div>
    </div>
  );
};

/* ── Footer Install Link ── */
export const PWAFooterInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(INSTALLED_KEY) === 'true' || window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
      return;
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setInstalled(true); localStorage.setItem(INSTALLED_KEY, 'true'); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setInstalled(true);
      localStorage.setItem(INSTALLED_KEY, 'true');
    }
    setDeferredPrompt(null);
  };

  if (installed) {
    return (
      <span className="text-emerald-400 flex items-center gap-1 text-sm">
        <Check className="w-4 h-4" /> App Installed
      </span>
    );
  }

  return (
    <button
      onClick={handleInstall}
      className="text-orange-400 hover:text-orange-300 flex items-center gap-1 text-sm transition-colors cursor-pointer"
      data-testid="pwa-footer-install"
    >
      <Download className="w-4 h-4" /> Install Desktop App
    </button>
  );
};

export default PWAInstallPrompt;
